/**
 * Auth utility module — thin wrapper around @auth0/auth0-react.
 *
 * The Auth0 SDK caches tokens in localStorage (Auth0's recommended
 * approach for SPAs with refresh tokens). Security is mitigated by
 * refresh token rotation and short access token lifetimes.
 *
 * This module provides:
 * 1. Auth0Provider config
 * 2. Module-level refs for non-React code (Apollo, Axios) to get tokens / logout
 * 3. Legacy AuthType enum for OAuth connection names
 */
import type { GetTokenSilentlyOptions, LogoutOptions } from '@auth0/auth0-react';

// ── Auth0 config ──────────────────────────────────────────────────────────────

const CLIENT_ID = import.meta.env.VITE_AUTH0_CLIENT_ID || '';
const DOMAIN = import.meta.env.VITE_AUTH0_DOMAIN || '';
const AUDIENCE = import.meta.env.VITE_AUTH0_AUDIENCE || '';

export const auth0Config = {
  domain: DOMAIN,
  clientId: CLIENT_ID,
  authorizationParams: {
    redirect_uri: typeof window !== 'undefined' ? window.location.origin + '/callback' : '',
    audience: AUDIENCE,
    scope: 'openid profile email offline_access',
  },
  useRefreshTokens: true,
  // localStorage is Auth0's recommended cacheLocation for SPAs with refresh tokens.
  // With memory-only cache, refresh tokens are lost on page refresh and the SDK
  // falls back to iframe silent auth — which fails in Safari/Firefox (ITP/ETP).
  // Security is mitigated by refresh token rotation (each use invalidates the
  // previous token) and short access token lifetimes.
  cacheLocation: 'localstorage' as const,
  useRefreshTokensFallback: true,
};

// ── OAuth connection types ────────────────────────────────────────────────────

export enum AuthType {
  GOOGLE = 'google-oauth2',
  MICROSOFT = 'microsoft-entra-id',
}

// ── Module-level refs for non-React code ──────────────────────────────────────
// These are set by AuthProvider on mount so that Apollo, Axios, and other
// non-component code can obtain tokens and trigger logout.

type GetTokenFn = (options?: GetTokenSilentlyOptions) => Promise<string>;
type LogoutFn = (options?: LogoutOptions) => Promise<void> | void;

let _getTokenSilently: GetTokenFn | null = null;
let _logout: LogoutFn | null = null;

// Resolved when setAuthRefs() is called for the first time. This lets non-React
// callers (Apollo auth link, axios interceptors) that fire during the very first
// render — before AuthGate's effect has run — await the token getter instead of
// silently sending an unauthenticated request.
let _refsReadyResolve: (() => void) | null = null;
const _refsReady: Promise<void> = new Promise((resolve) => {
  _refsReadyResolve = resolve;
});

export function setAuthRefs(
  getToken: GetTokenFn,
  logout: LogoutFn,
) {
  const wasUnset = _getTokenSilently === null;
  _getTokenSilently = getToken;
  _logout = logout;
  if (wasUnset && _refsReadyResolve) {
    _refsReadyResolve();
    _refsReadyResolve = null;
  }
}

/**
 * Get a valid access token. The SDK handles refresh automatically.
 * Pass `{ cacheMode: 'off' }` to force a refresh (e.g. after a 401).
 *
 * Returns undefined if no auth refs are set yet (app still initializing).
 */
export async function getTokenSilently(
  options?: GetTokenSilentlyOptions,
): Promise<string | undefined> {
  if (!_getTokenSilently) {
    // Race: Apollo / axios fired a request during the first render, before
    // AuthGate's effect ran. Wait briefly for setAuthRefs() instead of sending
    // an unauthenticated request that will fail and leave the UI blank.
    try {
      await Promise.race([
        _refsReady,
        new Promise<void>((_, reject) =>
          setTimeout(() => reject(new Error('auth refs timeout')), 5000),
        ),
      ]);
    } catch {
      console.warn('auth: getTokenSilently timed out waiting for AuthProvider');
      return undefined;
    }
    if (!_getTokenSilently) return undefined;
  }
  try {
    return await _getTokenSilently(options);
  } catch (error) {
    console.error('auth: getTokenSilently failed:', error);
    return undefined;
  }
}

/**
 * Trigger logout from non-React code.
 */
export function doLogout(options?: LogoutOptions) {
  if (!_logout) {
    console.warn('auth: doLogout called before AuthProvider mounted');
    return;
  }
  _logout(options);
}
